/* eslint-disable max-lines */
/**
 * ManagerReviewView — top-level Manager Review tool
 *
 * Manages onboarding steps 0–5 and the full assessment state for step 5.
 * Steps:
 *   0 = Welcome screen
 *   1 = Intro slides (4 slides explaining the 4 assessment sections)
 *   2 = Select Employee
 *   3 = Framework Preview
 *   4 = Before You Start (StepReady)
 *   5 = Assessment (tabs + sidebars)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import AccessDeniedScreen from '../../../components/AccessDeniedScreen';
import { useAuth } from '../../../contexts/AuthContext';
import { C, FINN_VALUES, RATING_SCALE, BIAS_CHECKS } from '../data/managerReviewData';
import {
  fetchDirectReports,
  NotAManagerError,
  PeopleDatabaseNotConfiguredError,
} from '../services/managerReviewApi';
import { setEmployeeNames } from '../components/manager-review/domainDictionary';
import { loadDraft, saveDraft, loadAllSubmittedDrafts } from '../services/managerReviewDraftService';
import type {
  Employee,
  Deliverable,
  BehaviorData,
  FeedbackItem,
  ActiveTab,
  ActiveSidebar,
} from '../types/managerReview';

// Onboarding step components
import StepWelcome from '../components/manager-review/onboarding/StepWelcome';
import StepIntro from '../components/manager-review/onboarding/StepIntro';
import StepSelectEmployee from '../components/manager-review/onboarding/StepSelectEmployee';
import StepFrameworkPreview from '../components/manager-review/onboarding/StepFrameworkPreview';
import StepReady from '../components/manager-review/onboarding/StepReady';
import { type CompletedReview } from '../components/manager-review/onboarding/StepComplete';
import StepAllReviews from '../components/manager-review/onboarding/StepAllReviews';

// Assessment components
import AssessmentNav from '../components/manager-review/AssessmentNav';
import DeliveryTab from '../components/manager-review/DeliveryTab';
import BehaviorsTab from '../components/manager-review/BehaviorsTab';
import SummaryTab from '../components/manager-review/SummaryTab';
import AIReviewTab, { type ParsedOutput } from '../components/manager-review/AIReviewTab';
import FrameworkSidebar from '../components/manager-review/FrameworkSidebar';
import FeedbackSidebar from '../components/manager-review/FeedbackSidebar';
import PromoFrameworkSidebar from '../components/manager-review/PromoFrameworkSidebar';

// Steps: 0=Welcome, 1=Intro, 2=SelectEmployee, 3=FrameworkPreview,
//        4=BeforeYouStart, 5=Assessment, 6=Complete, 7=AllReviews
type OnboardingStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Bump whenever the draft JSON shape changes in a breaking way.
// restoreFromDraft migrates older records to the current shape before applying state.
const DRAFT_SCHEMA_VERSION = 2;

/** Review cycle — part of the browser draft keys (the June 2026 keys had none). */
const MR_CYCLE = '2026-11';

const emptyDeliverable = (): Deliverable => ({
  title: '',
  rating: null,
  competencies: [],
  wellDone: '',
  improvement: '',
});

export default function ManagerReviewView() {
  const { user } = useAuth();
  const managerName = user?.displayName || user?.email?.split('@')[0] || '';

  // ── Direct reports (loaded from Airtable People database) ─────────────
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [isNotManager, setIsNotManager] = useState(false);

  // Fetch once on mount (user is already authenticated at this point)
  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEmployeesLoading(true);
    setEmployeesError(null);
    setIsNotManager(false);
    fetchDirectReports()
      .then(({ employees: data }) => {
        if (!cancelled) setEmployees(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAManagerError) {
          setIsNotManager(true);
        } else if (err instanceof PeopleDatabaseNotConfiguredError) {
          setEmployeesError('The People database connection is not set up yet. Contact the tool admin.');
        } else {
          setEmployeesError(err instanceof Error ? err.message : 'Failed to load direct reports.');
        }
      })
      .finally(() => { if (!cancelled) setEmployeesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Onboarding ──────────────────────────────────────────────────────────
  // Skip welcome + intro if the manager has been through onboarding before
  // Use user.email directly — App.tsx guarantees user is non-null before this
  // component renders, so the key is always a real email, never "undefined".
  const onboardingKey = `finn-mr-onboarded-${user?.email ?? ''}`;
  const hasOnboarded = !!user?.email && typeof window !== 'undefined' && !!localStorage.getItem(onboardingKey);
  const [step, setStep] = useState<OnboardingStep>(hasOnboarded ? 2 : 0);
  const [employee, setEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    if (employees.length > 0) {
      setEmployeeNames(employees.map(e => e.name));
    }
  }, [employees]);

  // ── Feedback / self-review (gathered during onboarding, shown in sidebar) ─
  const [selfReview, setSelfReview] = useState('');
  const [perfFeedbackRaw, setPerfFeedbackRaw] = useState('');
  const [anytimeFeedbackRaw, setAnytimeFeedbackRaw] = useState('');
  const [cultureAmpMode, setCultureAmpMode] = useState<null | 'read' | 'paste'>(null);
  // feedbackItems kept for AI-prompt backward-compat; no longer editable in StepReady
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);

  // ── Assessment state ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>(0);
  const [activeSidebar, setActiveSidebar] = useState<ActiveSidebar>(null);

  const [behaviorsData, setBehaviorsData] = useState<Record<string, BehaviorData>>({});
  const [behaviorsRating, setBehaviorsRating] = useState<number | null>(null);
  const [behaviorsRatingNote, setBehaviorsRatingNote] = useState('');

  const [deliverables, setDeliverables] = useState<Deliverable[]>([emptyDeliverable()]);
  const [deliveryRating, setDeliveryRating] = useState<number | null>(null);
  const [helpAnswer, setHelpAnswer] = useState<'yes' | 'no' | null>(null);

  const [promotion, setPromotion] = useState<'yes' | 'no' | null>(null);
  const [promotionNote, setPromotionNote] = useState('');
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [overallRatingNote, setOverallRatingNote] = useState('');
  const [promoTrack, setPromoTrack] = useState<'E' | 'M'>('E');

  const [biasChecked, setBiasChecked] = useState<Record<string, boolean>>({});
  const [biasNote, setBiasNote] = useState('');

  // ── AI Review generated output (persisted across tab switches) ────────────
  const [generatedReview, setGeneratedReview] = useState<ParsedOutput | null>(null);

  // ── Draft save state ──────────────────────────────────────────────────────
  const [draftStatus, setDraftStatus] = useState<'Draft' | 'Submitted' | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const airtableSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard: block Airtable saves while a draft is being loaded to prevent
  // an empty/reset form from overwriting real saved data during the load window.
  const isDraftLoadingRef = useRef(false);

  // ── Completed reviews — persisted to localStorage so hard refresh restores state ──
  // Browser keys carry the cycle, so last cycle's local drafts and completed list never reopen.
  const completedReviewsKey = `finn-mr-completed-${MR_CYCLE}-${user?.email ?? ''}`;
  const [completedReviews, setCompletedReviews] = useState<CompletedReview[]>(() => {
    try {
      const saved = localStorage.getItem(`finn-mr-completed-${MR_CYCLE}-${user?.email ?? ''}`);
      return saved ? (JSON.parse(saved) as CompletedReview[]) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(completedReviewsKey, JSON.stringify(completedReviews)); } catch { /* storage full */ }
  }, [completedReviewsKey, completedReviews]);

  // Hydrate completedReviews from Airtable once the employee list is ready.
  // localStorage gives an instant render; Airtable provides cross-device accuracy.
  useEffect(() => {
    if (employees.length === 0) return;
    let cancelled = false;

    loadAllSubmittedDrafts().then(remoteDrafts => {
      if (cancelled || remoteDrafts.length === 0) return;

      const isoToDisplay = (iso: string | null): string => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
      };

      const reconstructed: CompletedReview[] = [];
      for (const draft of remoteDrafts) {
        if (!draft.employeeId || !draft.draftJson) continue;
        try {
          const parsed = JSON.parse(draft.draftJson);
          // Match against loaded employee list; fall back to a minimal object so
          // previously-reviewed employees who are no longer in scope still appear.
          const emp = employees.find(e => e.id === draft.employeeId) ?? {
            id: draft.employeeId,
            name: draft.employeeName ?? draft.employeeId,
            email: '',
            title: '',
            level: parsed.level ?? '',
            track: '',
            role: '',
            avatar: (draft.employeeName ?? '?').split(' ').map((n: string) => n[0] ?? '').join('').slice(0, 2).toUpperCase(),
          };
          reconstructed.push({
            employee: emp,
            level: emp.level,
            deliveryRating: parsed.deliveryRating ?? null,
            behaviorsRating: parsed.behaviorsRating ?? null,
            overallRating: parsed.overallRating ?? null,
            promotion: parsed.promotion ?? null,
            completedAt: isoToDisplay(draft.lastSaved),
          });
        } catch { continue; }
      }

      if (reconstructed.length > 0) {
        setCompletedReviews(reconstructed);
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const [lastCompleted, setLastCompleted] = useState<{ managerFirst: string; employeeFirst: string } | null>(null);

  // ── Scroll to top on tab change ───────────────────────────────────────────
  // FINN Spaces uses a <main> overflow-auto container, not the window
  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'instant' });
    else window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  // ── Draft helpers ─────────────────────────────────────────────────────────

  const getDraftPayload = useCallback(() => ({
    schemaVersion: DRAFT_SCHEMA_VERSION,
    activeTab,
    behaviorsData,
    behaviorsRating,
    behaviorsRatingNote,
    deliverables,
    deliveryRating,
    helpAnswer,
    promotion,
    promotionNote,
    overallRating,
    overallRatingNote,
    promoTrack,
    biasChecked,
    biasNote,
    selfReview,
    perfFeedbackRaw,
    anytimeFeedbackRaw,
    feedbackItems,
    generatedReview,
    cultureAmpMode,
  }), [activeTab, behaviorsData, behaviorsRating, behaviorsRatingNote, deliverables,
    deliveryRating, helpAnswer, promotion, promotionNote, overallRating,
    overallRatingNote, promoTrack, biasChecked, biasNote, selfReview,
    perfFeedbackRaw, anytimeFeedbackRaw, feedbackItems, generatedReview, cultureAmpMode]);

  const restoreFromDraft = useCallback((json: string) => {
    try {
      const s = JSON.parse(json);
      const version = typeof s.schemaVersion === 'number' ? s.schemaVersion : 1;

      // ── Schema migrations ────────────────────────────────────────────────
      // v1 → v2: behaviorsData.observed was not always present; strengthsText /
      // improvementsText may be missing on older BehaviorData entries.
      // deliverables may be missing wellDone/improvement (renamed fields).
      if (version < 2) {
        if (s.behaviorsData && typeof s.behaviorsData === 'object') {
          for (const key of Object.keys(s.behaviorsData)) {
            const bd = s.behaviorsData[key] as Record<string, unknown>;
            bd.observed = (bd.observed && typeof bd.observed === 'object') ? bd.observed : {};
            bd.strengthsText = typeof bd.strengthsText === 'string' ? bd.strengthsText
              : Array.isArray(bd.strengths) ? (bd.strengths as string[]).join('\n') : '';
            bd.improvementsText = typeof bd.improvementsText === 'string' ? bd.improvementsText
              : Array.isArray(bd.improvements) ? (bd.improvements as string[]).join('\n') : '';
            bd.rating = typeof bd.rating === 'number' ? bd.rating : null;
          }
        }
        if (Array.isArray(s.deliverables)) {
          s.deliverables = (s.deliverables as Record<string, unknown>[]).map(d => ({
            ...d,
            rating: typeof d.rating === 'number' ? d.rating : null,
            wellDone: typeof d.wellDone === 'string' ? d.wellDone : '',
            improvement: typeof d.improvement === 'string' ? d.improvement : '',
            competencies: Array.isArray(d.competencies) ? d.competencies : [],
          }));
        }
      }
      // ── End migrations ───────────────────────────────────────────────────

      if (s.activeTab != null) setActiveTab(s.activeTab);
      if (s.behaviorsData) setBehaviorsData(s.behaviorsData);
      if (s.behaviorsRating != null) setBehaviorsRating(s.behaviorsRating);
      if (s.behaviorsRatingNote != null) setBehaviorsRatingNote(s.behaviorsRatingNote);
      if (s.deliverables) setDeliverables(s.deliverables);
      if (s.deliveryRating != null) setDeliveryRating(s.deliveryRating);
      if (s.helpAnswer != null) setHelpAnswer(s.helpAnswer);
      if (s.promotion != null) setPromotion(s.promotion);
      if (s.promotionNote != null) setPromotionNote(s.promotionNote);
      if (s.overallRating != null) setOverallRating(s.overallRating);
      if (s.overallRatingNote != null) setOverallRatingNote(s.overallRatingNote);
      if (s.promoTrack) setPromoTrack(s.promoTrack);
      if (s.biasChecked) setBiasChecked(s.biasChecked);
      if (s.biasNote != null) setBiasNote(s.biasNote);
      if (s.selfReview != null) setSelfReview(s.selfReview);
      if (s.perfFeedbackRaw != null) setPerfFeedbackRaw(s.perfFeedbackRaw);
      if (s.anytimeFeedbackRaw != null) setAnytimeFeedbackRaw(s.anytimeFeedbackRaw);
      if (s.feedbackItems) setFeedbackItems(s.feedbackItems);
      if (s.generatedReview) setGeneratedReview(s.generatedReview);
      if (s.cultureAmpMode != null) setCultureAmpMode(s.cultureAmpMode);
    } catch {
      // Corrupt draft — ignore and start fresh
    }
  }, []);

  // localStorage autosave — 800ms debounce on every state change in assessment
  useEffect(() => {
    if (step !== 5 || !employee) return;
    const lsKey = `finn-mr-draft-${MR_CYCLE}-${user?.email}-${employee.id}`;
    const timer = setTimeout(() => {
      try { localStorage.setItem(lsKey, JSON.stringify(getDraftPayload())); } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(timer);
  }, [step, employee, user?.email, getDraftPayload]);

  // Airtable save — 3s debounce, also fires immediately on tab switch
  const saveToAirtable = useCallback(async (emp: Employee, status?: 'Draft' | 'Submitted') => {
    if (!emp || isDraftLoadingRef.current) return;
    setIsSaving(true);
    const result = await saveDraft({
      employeeId: emp.id,
      employeeName: emp.name,
      draftJson: JSON.stringify(getDraftPayload()),
      status: status ?? draftStatus ?? 'Draft',
    });
    setIsSaving(false);
    if (result) {
      setLastSavedAt(result.lastSaved);
      setSaveError(false);
      if (status) setDraftStatus(status);
    } else {
      setSaveError(true);
    }
  }, [getDraftPayload, draftStatus]);

  // Trigger Airtable save when activeTab changes (immediate) or state changes (3s debounce)
  useEffect(() => {
    if (step !== 5 || !employee) return;
    if (airtableSaveTimer.current) clearTimeout(airtableSaveTimer.current);
    airtableSaveTimer.current = setTimeout(() => saveToAirtable(employee), 3000);
    return () => { if (airtableSaveTimer.current) clearTimeout(airtableSaveTimer.current); };
  }, [step, employee, getDraftPayload, saveToAirtable]);

  useEffect(() => {
    if (step !== 5 || !employee) return;
    if (airtableSaveTimer.current) clearTimeout(airtableSaveTimer.current);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    saveToAirtable(employee);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const selectEmployee = async (emp: Employee) => {
    isDraftLoadingRef.current = true;
    setEmployee(emp);
    // Reset to fresh state first (including feedback — will be restored from draft if available)
    setBehaviorsData({});
    setBehaviorsRating(null);
    setBehaviorsRatingNote('');
    setDeliverables([emptyDeliverable()]);
    setDeliveryRating(null);
    setPromotion(null);
    setSelfReview('');
    setPerfFeedbackRaw('');
    setAnytimeFeedbackRaw('');
    setFeedbackItems([]);
    setGeneratedReview(null);
    setDraftStatus(null);
    setLastSavedAt(null);
    setCultureAmpMode(null);

    // Sync localStorage check first — sets button text immediately so there's no flash
    const lsKey = `finn-mr-draft-${MR_CYCLE}-${user?.email}-${emp.id}`;
    const local = localStorage.getItem(lsKey);
    if (local) setDraftStatus('Draft');

    // Try Airtable (authoritative) — may override the localStorage status
    const remote = await loadDraft(emp.id);
    if (remote?.draftJson) {
      restoreFromDraft(remote.draftJson);
      setDraftStatus(remote.status);
      setLastSavedAt(remote.lastSaved);
      isDraftLoadingRef.current = false;
      return;
    }
    if (local) {
      restoreFromDraft(local);
      setDraftStatus('Draft');
    }
    isDraftLoadingRef.current = false;
  };

  const resetToEmployeeList = () => {
    setEmployee(null);
    setActiveTab(0);
    setActiveSidebar(null);
    setBehaviorsData({});
    setBehaviorsRating(null);
    setBehaviorsRatingNote('');
    setDeliverables([emptyDeliverable()]);
    setDeliveryRating(null);
    setPromotion(null);
    setPromotionNote('');
    setOverallRating(null);
    setOverallRatingNote('');
    setPromoTrack('E');
    setHelpAnswer(null);
    setBiasChecked({});
    setBiasNote('');
    setSelfReview('');
    setPerfFeedbackRaw('');
    setAnytimeFeedbackRaw('');
    setFeedbackItems([]);
    setGeneratedReview(null);
    setCultureAmpMode(null);
    setStep(2);
  };

  // Derived "done" flags
  const deliveryDone =
    deliverables.every((d) => d.rating !== null) && deliveryRating !== null;
  const behaviorsDone =
    FINN_VALUES.every((v) => behaviorsData[v.name]?.rating) &&
    behaviorsRating !== null;

  const computeSuggestedRating = (): number | null => {
    const bA = behaviorsRating ?? null;
    const dA = deliveryRating ?? null;
    if (!bA || !dA) return null;
    const avg = (bA + dA) / 2;
    const isTie = (avg * 10) % 5 === 0 && !Number.isInteger(avg);
    return isTie ? dA : Math.round(avg);
  };

  const computeSummaryDone = (): boolean => {
    const rdP = overallRating ? RATING_SCALE.find((r) => r.v === overallRating) : null;
    const ratingEligP = rdP ? rdP.promotion : false;
    const noConsecP = employee ? !employee.previouslyPromoted : true;
    const reasoningReqP =
      promotion === 'yes' && (!ratingEligP || !noConsecP);
    const biasAllCheckedP =
      Object.values(biasChecked).filter(Boolean).length === BIAS_CHECKS.length;
    const suggestedRatingP = computeSuggestedRating();
    const deviationP =
      overallRating && suggestedRatingP && overallRating !== suggestedRatingP;
    return (
      !!overallRating &&
      (!deviationP || !!overallRatingNote.trim()) &&
      promotion !== null &&
      biasAllCheckedP &&
      (!reasoningReqP || !!promotionNote.trim())
    );
  };

  const summaryDone = computeSummaryDone();

  const nextTabLabel = ['Behaviors', 'Summary', 'AI Review', ''][activeTab];

  const nextLocked = activeTab === 2 && !summaryDone;

  // ── Access gate: non-managers see a clear "not for you" screen ───────────
  if (isNotManager) {
    return (
      <AccessDeniedScreen
        title="Access denied"
        message={`The Manager Review tool is restricted to managers with active direct reports in the People database. If you think this is wrong, ask your People team to check that your email (${user?.email}) appears in the Supervisor Email column.`}
      />
    );
  }

  // ── Onboarding steps ──────────────────────────────────────────────────────
  if (step === 0) {
    return (
      <StepWelcome
        managerName={managerName}
        onNext={() => setStep(1)}
      />
    );
  }

  if (step === 1) {
    return (
      <StepIntro
        onNext={() => {
          localStorage.setItem(onboardingKey, '1');
          setStep(2);
        }}
        onBack={() => setStep(0)}
      />
    );
  }

  if (step === 2) {
    return (
      <StepSelectEmployee
        employees={employees}
        isLoading={employeesLoading}
        error={employeesError}
        selected={employee}
        hasDraft={draftStatus !== null}
        onSelect={selectEmployee}
        onNext={() => {
          if (!employee) return;
          // Skip framework + checks if a draft already exists or cultureAmpMode is set
          if (draftStatus !== null || cultureAmpMode !== null) { setStep(5); return; }
          const fwKey = `finn-mr-fw-visited-${employee.id}`;
          const fwVisited = !!localStorage.getItem(fwKey);
          setStep(fwVisited ? 4 : 3);
        }}
        onBack={() => setStep(1)}
        completedReviews={completedReviews}
        onViewAll={() => setStep(7)}
        onViewIntro={() => setStep(1)}
        onRevisitPrep={() => setStep(3)}
      />
    );
  }

  if (step === 3) {
    return (
      <StepFrameworkPreview
        employee={employee!}
        onNext={() => {
          localStorage.setItem(`finn-mr-fw-visited-${employee!.id}`, '1');
          setStep(4);
        }}
        onBack={() => setStep(2)}
      />
    );
  }

  if (step === 4) {
    return (
      <StepReady
        employee={employee!}
        onNext={() => setStep(5)}
        onBack={() => setStep(3)}
        selfReview={selfReview}
        setSelfReview={setSelfReview}
        perfFeedbackRaw={perfFeedbackRaw}
        setPerfFeedbackRaw={setPerfFeedbackRaw}
        anytimeFeedbackRaw={anytimeFeedbackRaw}
        setAnytimeFeedbackRaw={setAnytimeFeedbackRaw}
        cultureAmpMode={cultureAmpMode}
        setCultureAmpMode={setCultureAmpMode}
      />
    );
  }

if (step === 7) {
    return (
      <StepAllReviews
        completedReviews={completedReviews}
        lastCompleted={lastCompleted}
        onBack={() => setStep(2)}
        onStartNew={() => { setLastCompleted(null); resetToEmployeeList(); }}
      />
    );
  }

  // ── Step 5: Assessment ────────────────────────────────────────────────────
  if (!employee) return null;
  const level = employee.level;

  return (
    <div style={{ height: '100vh', background: '#1a1a1a', display: 'flex', overflow: 'hidden' }}>
      {/* Main content — shifts left when sidebar is open */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          marginRight: activeSidebar ? 420 : 0,
          transition: 'margin-right 0.25s',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <AssessmentNav
          employee={employee}
          level={level}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeSidebar={activeSidebar}
          setActiveSidebar={setActiveSidebar}
          deliveryDone={deliveryDone}
          behaviorsDone={behaviorsDone}
          summaryDone={summaryDone}
          draftStatus={draftStatus}
          lastSavedAt={lastSavedAt}
          isSaving={isSaving}
          saveError={saveError}
        />

        <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 1600, margin: '0 auto', padding: '24px 16px 60px' }}>
          {activeTab === 0 && (
            <DeliveryTab
              employee={employee}
              level={level}
              deliverables={deliverables}
              setDeliverables={setDeliverables}
              deliveryRating={deliveryRating}
              setDeliveryRating={setDeliveryRating}
              helpAnswer={helpAnswer}
              setHelpAnswer={setHelpAnswer}
              selfReview={selfReview}
              feedbackItems={feedbackItems}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
            />
          )}
          {activeTab === 1 && (
            <BehaviorsTab
              employee={employee}
              level={level}
              deliverables={deliverables}
              deliveryRating={deliveryRating}
              behaviorsData={behaviorsData}
              setBehaviorsData={setBehaviorsData}
              behaviorsRating={behaviorsRating}
              setBehaviorsRating={setBehaviorsRating}
              behaviorsRatingNote={behaviorsRatingNote}
              setBehaviorsRatingNote={setBehaviorsRatingNote}
              selfReview={selfReview}
              feedbackItems={feedbackItems}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
            />
          )}
          {activeTab === 2 && (
            <SummaryTab
              employee={employee}
              level={level}
              behaviorsData={behaviorsData}
              behaviorsRating={behaviorsRating}
              deliveryRating={deliveryRating}
              promotion={promotion}
              setPromotion={setPromotion}
              promotionNote={promotionNote}
              setPromotionNote={setPromotionNote}
              overallRating={overallRating}
              setOverallRating={setOverallRating}
              overallRatingNote={overallRatingNote}
              setOverallRatingNote={setOverallRatingNote}
              promoTrack={promoTrack}
              setPromoTrack={setPromoTrack}
              setActiveSidebar={setActiveSidebar}
              biasChecked={biasChecked}
              setBiasChecked={setBiasChecked}
              biasNote={biasNote}
              setBiasNote={setBiasNote}
            />
          )}
          {activeTab === 3 && (
            <AIReviewTab
              employee={employee}
              level={level}
              behaviorsData={behaviorsData}
              behaviorsRating={behaviorsRating}
              behaviorsRatingNote={behaviorsRatingNote}
              deliverables={deliverables}
              deliveryRating={deliveryRating}
              overallRating={overallRating}
              overallRatingNote={overallRatingNote}
              promotion={promotion}
              promotionNote={promotionNote}
              biasNote={biasNote}
              selfReview={selfReview}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
              feedbackItems={feedbackItems}
              generatedReview={generatedReview}
              setGeneratedReview={setGeneratedReview}
              onNavigate={(tab, anchor) => {
                setActiveTab(tab as ActiveTab);
                if (anchor) setTimeout(() => {
                  document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 150);
              }}
              onOpenFeedback={() => setActiveSidebar('feedback')}
            />
          )}

          {/* Bottom navigation */}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
            {activeTab > 0 ? (
              <button
                onClick={() => setActiveTab((t) => (t - 1) as ActiveTab)}
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                  border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                  borderRadius: 6, padding: '10px 20px',
                  background: 'transparent', color: '#fff',
                }}
              >
                ← Previous
              </button>
            ) : (
              <button
                onClick={() => {
                  if (window.confirm('Go back to employee selection? Your draft is saved and you can resume this review any time.')) resetToEmployeeList();
                }}
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                  border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                  borderRadius: 6, padding: '10px 20px',
                  background: 'transparent', color: 'rgba(255,255,255,0.6)',
                }}
              >
                ← Back to employees
              </button>
            )}

            {activeTab < 3 ? (
              <button
                onClick={() => !nextLocked && setActiveTab((t) => (t + 1) as ActiveTab)}
                title={
                  nextLocked
                    ? 'Please complete all fields in Summary to continue'
                    : undefined
                }
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                  border: 'none', cursor: nextLocked ? 'not-allowed' : 'pointer',
                  borderRadius: 6, padding: '10px 20px',
                  background: C.blue, color: '#fff',
                  opacity: nextLocked ? 0.45 : 1,
                }}
              >
                Next: {nextTabLabel} →
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <button
                  onClick={async () => {
                    if (!summaryDone) return;
                    await saveToAirtable(employee, 'Submitted');
                    const completedAt = (() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })();
                    setCompletedReviews(prev => {
                      const rest = prev.filter(r => r.employee.id !== employee.id);
                      return [...rest, { employee, level: employee.level, deliveryRating, behaviorsRating, overallRating, promotion, completedAt }];
                    });
                    setLastCompleted({ managerFirst: (managerName || '').split(' ')[0] || 'there', employeeFirst: employee.name.split(' ')[0] });
                    setStep(7);
                  }}
                  title={!summaryDone ? 'Complete the Summary tab before marking this review as done' : undefined}
                  style={{
                    fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                    border: 'none',
                    cursor: !summaryDone ? 'not-allowed' : 'pointer',
                    borderRadius: 6, padding: '10px 20px',
                    background: '#26bd59', color: '#fff',
                    opacity: !summaryDone ? 0.45 : 1,
                  }}
                >
                  ✓ Mark as Reviewed
                </button>
                <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>
                  You can still edit any time.
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Right sidebar */}
      {activeSidebar && (
        <div
          style={{
            position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
            background: '#fff', borderLeft: `1px solid ${C.border}`,
            overflowY: 'auto', zIndex: 90, boxShadow: '-4px 0 20px rgba(0,0,0,0.06)',
          }}
        >
          {/* Sticky header */}
          <div
            style={{
              position: 'sticky', top: 0, background: '#fff',
              borderBottom: `1px solid ${C.border}`,
              padding: '14px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              zIndex: 1,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f0f0f' }}>
              {activeSidebar === 'feedback' ? '💬 Multi-Source Feedback' : '📋 Talent Framework'}
            </div>
            <button
              onClick={() => setActiveSidebar(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 18, color: '#aaa', lineHeight: 1, padding: 4,
              }}
            >
              ✕
            </button>
          </div>

          {activeSidebar === 'framework' && (
            <FrameworkSidebar employee={employee} level={level} />
          )}
          {activeSidebar === 'feedback' && (
            <FeedbackSidebar
              selfReview={selfReview}
              setSelfReview={setSelfReview}
              perfFeedbackRaw={perfFeedbackRaw}
              setPerfFeedbackRaw={setPerfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
              setAnytimeFeedbackRaw={setAnytimeFeedbackRaw}
              employeeName={employee.name}
            />
          )}
          {activeSidebar === 'promoframework' && (
            <PromoFrameworkSidebar
              employee={employee}
              level={level}
              promoTrack={promoTrack}
              setPromoTrack={setPromoTrack}
            />
          )}
        </div>
      )}
    </div>
  );
}
